import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stethoscope, RefreshCw, Hash, DoorOpen, Check, Award, HeartPulse } from 'lucide-react';
import { doctorAPI, userAPI, specialityAPI } from '../services/api';
import { useToast } from './UIProvider';
import Select from './Select';
import TablePager, { usePagedRows } from './TablePager';

// ============================================================================
// Doctor Settings — per-doctor token prefix + consultation room.
//
// Token numbers are sequenced per-doctor-per-day (each doctor gets their own
// 1, 2, 3…). The prefix defined here is what makes that meaningful: a token for
// Dr Adeel with prefix "AB" issues as AB-1, AB-2… The room number feeds the
// Display Board so patients know where to go.
// ============================================================================
const DoctorSettingsManagement = ({ hasPermission }) => {
  const notify = useToast();

  const can = useCallback(
    (action) =>
      typeof hasPermission === 'function' ? hasPermission('SETTINGS_CLINICAL', action) : true,
    [hasPermission],
  );
  const canEdit = can('EDIT');

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-row working copy keyed by doctor id: { tokenPrefix, roomNumber, specialityId }.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [specialities, setSpecialities] = useState([]);
  // The doctor whose weekly clinic timings are open in the editor, or null.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await doctorAPI.getAll();
      const list = Array.isArray(data) ? data : [];
      // Alphabetical by display name — the app-wide default for user-facing lists.
      list.sort((a, b) =>
        (a.fullName || `${a.firstName} ${a.lastName}`).localeCompare(
          b.fullName || `${b.firstName} ${b.lastName}`,
        ),
      );
      setDoctors(list);
      const seeded = {};
      for (const d of list) {
        seeded[d.id] = {
          tokenPrefix: d.tokenPrefix || '',
          roomNumber: d.roomNumber || '',
          specialityId: d.specialityId || '',
          nurseOperational: d.nurseOperational === true,
        };
      }
      setDrafts(seeded);
    } catch (e) {
      notify('error', 'Failed to load doctors');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    specialityAPI
      .list()
      .then((list) => { if (!cancelled) setSpecialities(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setSpecialities([]); });
    return () => { cancelled = true; };
  }, []);

  const setField = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  // A row is dirty when its draft differs from the doctor's saved values.
  const isDirty = useCallback(
    (doc) => {
      const d = drafts[doc.id] || {};
      return (
        (d.tokenPrefix || '') !== (doc.tokenPrefix || '') ||
        (d.roomNumber || '') !== (doc.roomNumber || '') ||
        (d.specialityId || '') !== (doc.specialityId || '') ||
        (d.nurseOperational === true) !== (doc.nurseOperational === true)
      );
    },
    [drafts],
  );

  // `extra` carries what is saved from outside the row, the clinic timings from their editor.
  // Returns true once saved, so the editor knows it can close.
  const handleSave = async (doc, extra = {}) => {
    const d = drafts[doc.id] || {};
    const prefix = (d.tokenPrefix || '').trim().toUpperCase();
    if (prefix && !/^[A-Z0-9]{1,10}$/.test(prefix)) {
      notify('error', 'Token prefix must be 1–10 letters or digits, no spaces or symbols');
      return false;
    }
    setSavingId(doc.id);
    try {
      const updated = await userAPI.updateDoctorSettings(doc.id, {
        tokenPrefix: d.tokenPrefix || '',
        roomNumber: d.roomNumber || '',
        specialityId: d.specialityId || '',
        nurseOperational: d.nurseOperational === true,
        ...extra,
      });
      // Reflect the server's normalised values (prefix upper-cased, blanks → null).
      setDoctors((prev) => prev.map((x) => (x.id === doc.id ? { ...x, ...updated } : x)));
      setDrafts((prev) => ({
        ...prev,
        [doc.id]: {
          tokenPrefix: updated.tokenPrefix || '',
          roomNumber: updated.roomNumber || '',
          specialityId: updated.specialityId || '',
          nurseOperational: updated.nurseOperational === true,
        },
      }));
      notify('success', `Saved settings for ${updated.fullName || doc.fullName}`);
      // Turning a nurse on/off changes whether the Nurse Station menu has anything behind it.
      window.dispatchEvent(new Event('clinical-settings:changed'));
      return true;
    } catch (e) {
      notify('error', e.message || 'Failed to save doctor settings');
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const doctorName = (d) => d.fullName || `${d.firstName || ''} ${d.lastName || ''}`.trim();

  const sampleToken = useMemo(() => {
    return (id) => {
      const p = (drafts[id]?.tokenPrefix || '').trim().toUpperCase();
      return p ? `${p}-1` : '1';
    };
  }, [drafts]);

  // A doctor's unsaved changes are kept by id, so turning the page does not lose them.
  const paging = usePagedRows(doctors, { id: 'doctor-settings' });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-12 text-center">
          <RefreshCw className="h-8 w-8 text-emerald-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading doctors…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
              <Stethoscope className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Doctor Settings</h1>
              <p className="text-gray-600">Speciality, token prefix, room, clinic timings and nurse vitals per doctor</p>
            </div>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {/* Explainer */}
        <div className="bg-white rounded-2xl shadow-xl p-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Set a <span className="font-medium">token prefix</span> for each doctor. Tokens are
            numbered per doctor each day, so Dr&nbsp;Adeel with prefix{' '}
            <span className="font-mono font-semibold text-emerald-700">AB</span> issues tokens as{' '}
            <span className="font-mono font-semibold text-emerald-700">AB-1, AB-2…</span> The{' '}
            <span className="font-medium">room number</span> tells patients where the doctor is
            sitting and will appear on the Display Board. The{' '}
            <span className="font-medium">speciality</span> decides which instruction chips and quick
            lab tests this doctor sees while writing a prescription. The{' '}
            <span className="font-medium">clinic timings</span> are the days and hours the doctor sits,
            printed on the prescription and the token slip and shown on the Display Board.
          </p>
        </div>

        {/* Doctors table. On a narrow window it scrolls inside its card rather than running out of
            it; the Select dropdowns open in a portal, so the scroller cannot clip them. The count
            bars stay outside the scroller so they do not slide away with the columns. */}
        <div className="bg-white rounded-2xl shadow-xl">
          {doctors.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No doctors found.</div>
          ) : (
            <>
            <TablePager paging={paging} noun="doctor" className="px-4" />
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-4">Doctor</th>
                  <th className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5">
                      <Award className="h-3.5 w-3.5" /> Speciality
                    </span>
                  </th>
                  <th className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5">
                      <Hash className="h-3.5 w-3.5" /> Token Prefix
                    </span>
                  </th>
                  <th className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5">
                      <DoorOpen className="h-3.5 w-3.5" /> Room Number
                    </span>
                  </th>
                  <th className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5">
                      <HeartPulse className="h-3.5 w-3.5" /> Nurse Vitals
                    </span>
                  </th>
                  <th className="px-4 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paging.pageRows.map((doc) => {
                  const draft = drafts[doc.id] || {};
                  const dirty = isDirty(doc);
                  const saving = savingId === doc.id;
                  return (
                    <tr key={doc.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">{doctorName(doc)}</div>
                        {doc.specialization && (
                          <div className="text-xs text-gray-500">{doc.specialization}</div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <Select
                          value={draft.specialityId || ''}
                          onChange={(v) => setField(doc.id, 'specialityId', v)}
                          disabled={!canEdit || saving}
                          className="w-44"
                          buttonClassName="p-2 border-2 rounded-lg"
                          options={[
                            // Once assigned, a speciality can be changed but not blanked here —
                            // doctors always carry one. Use Edit User to change it wholesale.
                            { value: '', label: 'Not set', disabled: !!doc.specialityId },
                            // Keep the doctor's current speciality selectable even if it was
                            // deactivated in the catalog, so saving doesn't silently clear it.
                            ...(doc.specialityId && !specialities.some((s) => s.id === doc.specialityId)
                              ? [{ value: doc.specialityId, label: doc.specialityName || doc.specialization }]
                              : []),
                            ...specialities.map((s) => ({ value: s.id, label: s.name })),
                          ]}
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={draft.tokenPrefix || ''}
                            onChange={(e) =>
                              setField(doc.id, 'tokenPrefix', e.target.value.toUpperCase())
                            }
                            disabled={!canEdit || saving}
                            maxLength={10}
                            placeholder="—"
                            className="w-20 p-2 border-2 border-gray-200 rounded-lg uppercase font-mono tracking-wide focus:border-emerald-400 focus:outline-none disabled:opacity-50"
                          />
                          <span className="text-xs text-gray-400 font-mono whitespace-nowrap">
                            → {sampleToken(doc.id)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          value={draft.roomNumber || ''}
                          onChange={(e) => setField(doc.id, 'roomNumber', e.target.value)}
                          disabled={!canEdit || saving}
                          maxLength={50}
                          placeholder="—"
                          className="w-20 p-2 border-2 border-gray-200 rounded-lg focus:border-emerald-400 focus:outline-none disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-4">
                        {/* Per doctor: on means their patients wait in the Nurse Station queue for
                            vitals first; off means they go straight to this doctor, who records
                            vitals when starting the prescription. */}
                        <button
                          type="button"
                          onClick={() => setField(doc.id, 'nurseOperational', !(draft.nurseOperational === true))}
                          disabled={!canEdit || saving}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${
                            draft.nurseOperational === true ? 'bg-emerald-600' : 'bg-gray-300'
                          } ${(!canEdit || saving) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          aria-label={draft.nurseOperational === true ? 'Nurse records vitals for this doctor' : 'This doctor records their own vitals'}
                          title={draft.nurseOperational === true ? 'Nurse records vitals before the doctor sees the patient' : 'Patients go straight to this doctor, who records vitals at prescription start'}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            draft.nurseOperational === true ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </td>
                      <td className="px-4 py-4 text-right">
                        {canEdit ? (
                          <button
                            onClick={() => handleSave(doc)}
                            disabled={!dirty || saving}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium transition-colors ${
                              dirty && !saving
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {saving ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 italic">View only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <TablePager paging={paging} noun="doctor" position="bottom" className="px-4" />
            </>
          )}
        </div>

        {!canEdit && (
          <p className="text-xs text-gray-500 italic">
            You don't have permission to change these settings.
          </p>
        )}
      </div>
    </div>
  );
};

export default DoctorSettingsManagement;
